<template>
  <div class="kpt-device-detail" v-if="device && !isLoading">
    <vue-headful :title="title" />
    <header class="kpt-detail-pane-header">
      <h4>
        <b-tooltip label="Refreshing..." v-if="isRefreshing">
          <font-awesome-icon :icon="['far', 'spinner-third']" spin />
        </b-tooltip>
        <font-awesome-icon :icon="['far', 'microchip']" v-else />
        <span>{{device.serial}}</span>
      </h4>
      <div class="page-control block">
        <!-- <font-awesome-icon :icon="['far', 'thumbtack']" @click="pinToDash('device', device)" title="Pin to Dashboard" /> -->
        <font-awesome-icon :icon="['far', 'times-circle']" size="lg" @click="close" title="Close" />
      </div>
    </header>
    <div class="kpt-detail-main">
      <div class="kpt-detail-nav">
        <b-field>
          <b-input v-model="menuFilterTerm" type="text" size="is-small" placeholder="Search device..." />
        </b-field>
        <header v-if="isVisible('deviceOverview')">
          <h6 class="is-uppercase"><strong>General</strong></h6>
        </header>
        <ul>
          <router-link 
          tag="li" 
          :to="{ name: 'deviceOverview' }" 
          ref="deviceOverview"
          active-class="is-active"
          :keywords="['overview']" 
          v-show="isVisible('deviceOverview')">
            <a>
              <font-awesome-icon :icon="['far', 'file-chart-line']" />
              <span>Overview</span>
            </a>
          </router-link>
        </ul>
        <header v-if="isVisible('deviceFeatures') || isVisible('deviceSoftware')">
          <h6 class="is-uppercase"><strong>Settings</strong></h6>
        </header>
        <ul>
          <router-link 
          tag="li" 
          :to="{ name: 'deviceFeatures' }" 
          ref="deviceFeatures"
          active-class="is-active"
          :keywords="['features', 'certificate', 'certificates', 'gps', 'privacy', 'private', 'slr', 'slr trial']" 
          v-show="isVisible('deviceFeatures')"
          v-if="$hasRole('Super User,Kymeta Admin,Product Support')">
            <a>
              <font-awesome-icon :icon="['far', 'sliders-h']" />
              <span>Features</span>
            </a>
          </router-link>
          <router-link 
          tag="li" 
          :to="{ name: 'deviceSoftware' }" 
          ref="deviceSoftware"
          active-class="is-active"
          :keywords="['software', 'update', 'version', 'locked', 'lock', 'target']" 
          v-show="isVisible('deviceSoftware')"
          v-if="isDeviceTypeAntenna">
            <a>
              <font-awesome-icon :icon="['far', 'microchip']" />
              <span>Software</span>
            </a>
          </router-link>
          <router-link 
            tag="li" 
            :to="{ name: 'deviceGroups' }" 
            ref="deviceGroups" 
            active-class="is-active" 
            :keywords="['groups', 'device']" 
            v-show="isVisible('deviceGroups')"
            v-if="$hasRole('Super User,Kymeta Admin,Sales Support,Product Support,Account Admin,Credentialed')">
            <a>
              <font-awesome-icon :icon="['far', 'object-group']" />
              <span>Groups</span>
            </a>
          </router-link>
        </ul>
        <header v-if="isVisible('deviceActivity')">
          <h6 class="is-uppercase"><strong>Monitoring</strong></h6>
        </header>
        <ul>
          <!-- <router-link tag="li" :to="{ name: 'deviceMetrics' }" active-class="is-active">
            <a>
              <font-awesome-icon :icon="['far', 'chart-area']" />
              <span>Metrics (NYI)</span>
            </a>
          </router-link> -->
          <router-link 
          tag="li" 
          :to="{ name: 'deviceActivity' }" 
          ref="deviceActivity"
          active-class="is-active"
          :keywords="['activity', 'event', 'history']" 
          v-show="isVisible('deviceActivity')">
            <a>
              <font-awesome-icon :icon="['far', 'list-ul']" />
              <span>Activity</span>
            </a>
          </router-link>
        </ul>
        <!-- <header>
          <h6 class="is-uppercase"><strong>Support</strong></h6>
        </header>
        <ul>
          <router-link tag="li" :to="{ name: 'deviceSupport' }" active-class="is-active">
            <a>
              <font-awesome-icon :icon="['far', 'user-headset']" />
              <span>Contact Support</span>
            </a>
          </router-link>
        </ul> -->
      </div>
      <div class="kpt-detail-content">
        <router-view></router-view>
      </div>
    </div>
  </div>
  <div v-else-if="!device && !isLoading && notFound">
    <vue-headful title="Device Not Found" />
    <header class="kpt-detail-pane-header">
      <h4>
        <span>Device Not Found</span>
      </h4>
      <div class="page-control block">
        <font-awesome-icon :icon="['far', 'times-circle']" size="lg" @click="close" title="Close" />
      </div>
    </header>
    <div class="kpt-detail-main">
      <div class="kpt-detail-content" style="padding: 12px">
        <p>This device does not exist or you don't have the permission to view this device. If you've arrived here via a hotlink, please report this to Kymeta.</p>
        <br>
        <p>Go to the <a @click="close">device list</a> page.</p>
      </div>
    </div>
  </div>
  <div class="kpt-detail-loading" v-else>
    <b-loading :is-full-page="false" :active.sync="isLoading" :can-cancel="false"></b-loading>
  </div>
</template>
<script>
import { pinToDashMixin } from '../shared/mixins/pinToDash';

export default {
  data() {
    return {
      notFound: false,
      menuFilterTerm: ''
    }
  },
  beforeRouteUpdate(to, from, next) {
    if (to.params.id != from.params.id) {   
      this.$store.dispatch('fetchDevice', to.params.id);
    }
    next();
  },
  async created() {
    if (this.$route.params.id != 'create') {
      const result = await this.$store.dispatch('fetchDevice', this.$route.params.id);
      if (!result) {
        this.notFound = true;
        return;
      }
      if (this.device && this.device.software && this.device.software.currentVersion) {
        await this.$store.dispatch('fetchDeviceSoftwareVersionsBySerial', { serial: this.device.serial, currentVersion: this.device.software.currentVersion })
      }
    }
  },
  mixins: [pinToDashMixin],
  computed: {
    device() {
      return this.$store.state.devices.device;
    },
    isLoading() {
      return this.$store.state.devices.deviceLoading;
    },
    isRefreshing() {
      return this.$store.state.devices.deviceRefreshing;
    },
    title() {
      return `Device - ${this.device.serial}`;
    },
    visibleMenuItems() {
      const inputValue = this.menuFilterTerm;
      const visibleArray = [];
      // eslint-disable-next-line no-restricted-syntax, guard-for-in
      for (const ref in this.$refs) {
        const kw = this.$refs[ref].$el.getAttribute('keywords');
        const kwArray = kw.split(',');
        if (kwArray.includes(inputValue)) visibleArray.push(ref);
      }
      return visibleArray;
    },
    isDeviceTypeAntenna() {
      return this.device.deviceType == 'Antenna';
    }
  },
  methods: {
    close() {
      this.$router.push({ path: '/devices' });
    },
    isVisible(name) {
      return this.visibleMenuItems.includes(name) || !this.visibleMenuItems.length;
    }
  }
}
</script>
<style lang="scss" scoped>
.kpt-device-detail {
  height: 100%;
  display: flex;
  flex-direction: column;
}
</style>
